/**
 * Who may read HR's screens, and who may only read them.
 *
 * An approver (the owner) sees everything HR sees and approves what waits on
 * HR (leave, salary sheets, letters, new joiners, resignations, change
 * requests) but changes no record. The screens hide what would change one;
 * the server refuses it all the same.
 */
import type { Session } from "@/lib/api/types";
import { useAuth } from "@/lib/auth/AuthProvider";

type Access = Pick<Session, "is_approver" | "can_view_admin"> & {
  user: Pick<Session["user"], "role">;
};

/** An administrator, or an approver: HR's screens open for them. */
export function canViewAdmin(session: Access | null | undefined): boolean {
  if (!session) return false;
  return session.user.role === "admin" || Boolean(session.can_view_admin ?? session.is_approver);
}

/** An approver who is not also HR: reads and approves, changes nothing. */
export function isReadOnly(session: Access | null | undefined): boolean {
  if (!session) return false;
  return session.user.role !== "admin" && Boolean(session.is_approver);
}

/** Whether this person may approve or decline what waits on HR. */
export function canApprove(session: Access | null | undefined): boolean {
  return canViewAdmin(session);
}

/** True for an approver on HR's screens: hide what changes a record. */
export function useReadOnly(): boolean {
  return isReadOnly(useAuth().session);
}

export function useCanViewAdmin(): boolean {
  return canViewAdmin(useAuth().session);
}
